import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import client from '../api/client';
import { loginRequest } from '../config/msalConfig';
import { appInsights } from '../lib/appInsights';

function setAiUser(u) {
  if (!appInsights) return;
  if (!u) {
    appInsights.clearAuthenticatedUserContext();
    return;
  }
  // Sanitize: AI rejects authenticatedUserId with commas, semicolons, equals, spaces.
  const safeId = String(u.id).replace(/[,;= ]/g, '_');
  appInsights.setAuthenticatedUserContext(safeId, u.email, true);
}

const AuthContext = createContext(null);

/**
 * Normalize user object to always have `roles` array and backward-compat `role`.
 * Handles both old (role: 'owner') and new (roles: ['admin','supervisor']) JWT payloads.
 */
function normalizeUser(u) {
  if (!u) return u;

  let roles = u.roles || [];
  if (roles.length === 0 && u.role) {
    if (u.role === 'owner') {
      roles = ['admin', 'supervisor', 'employee'];
    } else {
      roles = [u.role];
    }
  }

  const role = u.role || (roles.includes('admin') ? 'admin' : (roles.includes('reviewer') ? 'reviewer' : (roles.includes('supervisor') ? 'supervisor' : 'employee')));
  return { ...u, roles, role };
}

export function AuthProvider({ children }) {
  const { instance: msalInstance, accounts } = useMsal();
  const msalAuthenticated = useIsAuthenticated();
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);
  const ssoAttempted = useRef(false);

  const [ssoError, setSsoError] = useState(null);

  // Check existing JWT on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }
    client.get('/api/auth/me')
      .then((r) => {
        const u = normalizeUser(r.data);
        setUser(u);
        setAiUser(u);
      })
      .catch(() => localStorage.removeItem('token'))
      .finally(() => setLoading(false));
  }, []);

  // When MSAL detects an authenticated account (after redirect), exchange for app JWT
  useEffect(() => {
    if (!msalAuthenticated || accounts.length === 0) return;
    if (localStorage.getItem('token') || ssoAttempted.current) return;
    ssoAttempted.current = true;

    (async () => {
      try {
        const silentResult = await msalInstance.acquireTokenSilent({
          scopes: ['openid', 'profile', 'email'],
          account: accounts[0],
        });
        if (silentResult?.idToken) {
          const res = await client.post('/api/auth/sso', { idToken: silentResult.idToken });
          localStorage.setItem('token', res.data.token);
          const u = normalizeUser(res.data.user);
          setUser(u);
          setAiUser(u);
        }
      } catch (err) {
        console.error('SSO auto-login error:', err);
        // Silent acquisition can fail when the Microsoft session cookie is stale:
        // the hidden renew iframe never completes and MSAL throws `timed_out`
        // (or interaction_required). The cached account is then useless and keeps
        // timing out on every load, leaving the user stuck on the login screen.
        // Clear it so the login button triggers a clean interactive sign-in.
        const silentFailCodes = ['timed_out', 'monitor_window_timeout', 'interaction_required', 'login_required', 'consent_required'];
        if (silentFailCodes.includes(err?.errorCode)) {
          await msalInstance.clearCache().catch(() => {});
          setSsoError('Tu sesión de Microsoft expiró. Vuelve a iniciar sesión.');
        } else {
          setSsoError(err.response?.data?.error || err.message || 'Error al autenticar con el servidor.');
        }
      }
    })();
  }, [msalAuthenticated, accounts]);

  /** SSO login — triggers Microsoft redirect */
  function loginWithSSO() {
    setSsoError(null);
    // loginRedirect REJECTS (instead of navigating to Microsoft) when MSAL has
    // stale "interaction_in_progress" state — common after a new deploy or an
    // interrupted prior attempt. Previously this had no .catch(), so the button
    // silently did nothing and the user stayed on the login screen. Surface the
    // error, and auto-recover from stale interaction state by clearing the MSAL
    // cache and retrying once.
    msalInstance.loginRedirect(loginRequest).catch((err) => {
      console.error('SSO loginRedirect error:', err);
      if (err?.errorCode === 'interaction_in_progress') {
        msalInstance.clearCache()
          .catch(() => {})
          .finally(() => {
            msalInstance.loginRedirect(loginRequest).catch((e) => {
              console.error('SSO loginRedirect retry failed:', e);
              setSsoError('No se pudo iniciar sesión con Microsoft. Cierra otras pestañas del portal e intenta de nuevo.');
            });
          });
      } else {
        setSsoError(err?.message || 'No se pudo iniciar sesión con Microsoft.');
      }
    });
  }

  /** Password login — fallback for accounts not in the Azure AD tenant */
  async function login(email, password) {
    const res = await client.post('/api/auth/login', { email, password });
    localStorage.setItem('token', res.data.token);
    const u = normalizeUser(res.data.user);
    setUser(u);
    setAiUser(u);
    return u;
  }

  function logout() {
    localStorage.removeItem('token');
    setUser(null);
    setAiUser(null);
    // Also sign out of Microsoft session if active
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.logoutRedirect({ postLogoutRedirectUri: '/login' }).catch(() => {});
    }
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithSSO, logout, ssoError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

/** Utility: check if user has a specific role */
export function userHasRole(user, role) {
  return user?.roles?.includes(role) ?? false;
}

export function userIsAdmin(user) {
  return userHasRole(user, 'admin');
}

export function userIsSupervisor(user) {
  return userHasRole(user, 'supervisor') || userHasRole(user, 'admin');
}

export function userIsReviewer(user) {
  return userHasRole(user, 'reviewer') || userHasRole(user, 'admin');
}

export function userIsEngineer(user) {
  return userHasRole(user, 'engineer') || userHasRole(user, 'admin');
}

export function userIsManager(user) {
  return userHasRole(user, 'manager') || userHasRole(user, 'admin');
}

export function userIsAccountant(user) {
  return userHasRole(user, 'accountant') || userHasRole(user, 'admin');
}

/**
 * V61 — ⚠ EL ÚNICO HELPER DE ROL QUE **NO** INCLUYE A ADMIN, Y ES DELIBERADO.
 *
 * Los otros cinco hacen `|| admin` porque el admin puede EJERCER esas funciones en la
 * app. Ser socio(a) no es una función que se ejerza: es una ASIGNACIÓN por proyecto.
 * Quién cierra la cadena de PTO lo resuelve la ganga de ESE proyecto (V60,
 * `resolveGangaApprovers`), y ser admin no convierte a nadie en socio de un proyecto.
 * Con `|| admin` este helper diría "sí" para cualquier administrador y cualquier
 * pantalla que lo usara mentiría.
 *
 * ⚠⚠ NO LO USES PARA AUTORIZAR UNA APROBACIÓN DE PTO. Es para presentación (etiqueta del
 * rol, ruteo por defecto, qué módulos se ven). La autorización real es del backend y sale
 * de la ganga; el frontend no tiene con qué decidirla.
 */
export function userIsSocio(user) {
  return userHasRole(user, 'socio');
}

/**
 * 🔴 V65 — QUIÉN ALCANZA EL MÓDULO/PESTAÑA DE VACACIONES. Vive acá, con los demás
 * helpers, porque lo consultan DOS lugares que no se ven entre sí: el `check` del módulo
 * en PortalLayout (el sidebar) y la barra de pestañas del dashboard.
 *
 * ⚠⚠ ESA SEPARACIÓN YA SE COBRÓ UN BUG: al retirarle Vacaciones al chofer se tocó el
 * sidebar y NO la pestaña, así que la seguía viendo en el dashboard. Son dos superficies
 * del mismo permiso; con la regla escrita dos veces, arreglar una deja la otra.
 *
 * El chofer (employee a secas) queda afuera: no solicita — se la registra quien conduce
 * su ganga.
 */
/**
 * 🔴 V65 — QUIÉN ALCANZA NÓMINA. Espeja el `check` del módulo Horas en PortalLayout.
 *
 * ⚠⚠ CUARTA VEZ DEL MISMO PATRÓN: se agrega un rol al módulo y la pestaña del dashboard
 * se queda atrás, porque la regla estaba escrita en dos lugares que no se ven. Socio y
 * contable cargan su propia nómina desde V65 y seguían sin ver la pestaña. Igual que
 * `userVeVacaciones`: una función, dos superficies.
 */
export function userVeNomina(user) {
  return userIsSupervisor(user) || userIsEngineer(user) || userIsManager(user)
      || userIsReviewer(user) || userIsAdmin(user)
      || userIsSocio(user) || userIsAccountant(user);
}

/**
 * 🔴 V82 — QUIÉN VE GASTOS DE SUS PROYECTOS (no solo lo propio).
 *
 * Espeja `veMasQueLoSuyo` de `receiptController`, que a su vez sale del alcance de
 * `projectScopeService`. **UNA función, dos superficies**: el sub-link del menú y el guard de
 * la ruta `/supervisor/*`. Escribir la lista en los dos lados es lo que dejó al socio afuera
 * TRES veces seguidas — en `getReceipts` (V76), en el detalle (V81) y casi en el menú.
 *
 * ⚠ VER NO ES APROBAR. Para los botones de acción está `userApruebaGastos`, que NO incluye al
 * socio: en Gastos su rol es de seguimiento.
 */
export function userVeGastosDeSusProyectos(user) {
  return userIsSupervisor(user) || userIsEngineer(user) || userIsManager(user)
      || userIsReviewer(user) || userIsAdmin(user)
      || userIsSocio(user) || userIsAccountant(user);
}

/**
 * 🔴 Quién puede APROBAR / DEVOLVER / RECHAZAR en Gastos. **El socio NO está**, y esa ausencia
 * es la decisión: VE por seguimiento y no aprueba.
 *
 * ⚠ Sin esto, el socio entra a la pantalla de revisión y **le aparecen los botones**: el
 * backend los rechaza con 403, así que no es un agujero de seguridad — es AUTORIDAD APARENTE,
 * que es peor que no tener el botón. Un control que parece accionable y falla enseña a
 * desconfiar de la pantalla.
 */
export function userApruebaGastos(user) {
  return userIsSupervisor(user) || userIsEngineer(user) || userIsManager(user)
      || userIsReviewer(user) || userIsAdmin(user);
}

export function userVeVacaciones(user) {
  return userIsSupervisor(user) || userIsEngineer(user) || userIsManager(user)
      || userIsReviewer(user) || userIsSocio(user) || userIsAccountant(user)
      || userIsAdmin(user);
}


/** @deprecated — kept for backward compat, always returns false now */
export function userIsAccounting(user) {
  return false;
}

/** @deprecated — kept for backward compat, always returns false now */
export function userIsPtoApprover(user) {
  return false;
}
